import React, { useCallback, useEffect, useRef } from 'react';

import * as styles from './BlurredBlobs.module.scss';

// Linear interpolation function
const lerp = (start, end, time) => start + (end - start) * time;

// Check if the device is iOS
const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(window.navigator.userAgent);

// BlurredBlobs component
export default function BlurredBlobs({
  container,
  primaryColor = ' #EE9528',
  secondaryColor = '#9E36F1',
  primaryBlobSize = 250,
  secondaryBlobSize = 200,
}) {
  // References to canvas, context, blobs and mouse
  const canvas = useRef();
  const ctx = useRef();
  const primaryBlob = useRef();
  const secondaryBlob = useRef();
  const mouse = useRef();

  // Set the size of the canvas and initial positions of blobs and mouse
  const setSize = useCallback(() => {
    if (!canvas.current) return;
    canvas.current.width = container.current.offsetWidth;
    canvas.current.height = container.current.offsetHeight;
    primaryBlob.current = mouse.current = { x: canvas.current.width * 0.65, y: canvas.current.height * 0.55 };
    secondaryBlob.current = { x: primaryBlob.current.x + 200, y: primaryBlob.current.y - 100 };
  }, [container]);

  // Update mouse position on mouse move
  const followMouse = useCallback((e) => {
    mouse.current.x = e.x;
    mouse.current.y = e.y;
  }, []);

  // Reset blobs position on mouse out
  const resetBlobs = useCallback(() => {
    mouse.current = { x: canvas.current.width * 0.65, y: canvas.current.height * 0.65 };
  }, []);

  // Draw blobs on canvas
  function draw() {
    // Draw primary blob
    const primaryX = lerp(primaryBlob.current.x, mouse.current.x, 0.025);
    const primaryY = lerp(primaryBlob.current.y, mouse.current.y, 0.02);
    ctx.current.fillStyle = primaryColor;
    ctx.current.beginPath();
    ctx.current.arc(primaryX, primaryY, primaryBlobSize, 0, Math.PI * 2);
    ctx.current.closePath();
    ctx.current.fill();
    primaryBlob.current = { x: primaryX, y: primaryY };

    // Draw secondary blob
    const secondaryX = lerp(secondaryBlob.current.x, primaryBlob.current.x + 200, 0.01);
    const secondaryY = lerp(secondaryBlob.current.y, primaryBlob.current.y - 100, 0.01);
    ctx.current.fillStyle = secondaryColor;
    ctx.current.beginPath();
    ctx.current.arc(secondaryX, secondaryY, secondaryBlobSize, 0, Math.PI * 2);
    ctx.current.closePath();
    ctx.current.fill();
    secondaryBlob.current = { x: secondaryX, y: secondaryY };
  }

  // Animation loop
  function animate() {
    if (canvas.current) {
      ctx.current.clearRect(0, 0, canvas.current.width, canvas.current.height);
      draw();
      requestAnimationFrame(animate);
    }
  }

  // Initialize canvas, blobs and mouse, and start animation on mount
  useEffect(() => {
    if (!container.current) return;
    ctx.current = canvas.current.getContext('2d');
    setSize();
    container.current.addEventListener('mousemove', followMouse);
    container.current.addEventListener('mouseout', resetBlobs);
    animate();

    // Clean up event listeners on unmount
    return () => {
      container.current?.removeEventListener('mousemove', followMouse);
      container.current?.removeEventListener('mouseout', resetBlobs);
    };
  }, [container, followMouse, resetBlobs, setSize]);

  // Update canvas size on window resize
  useEffect(() => {
    if (isIOS) return;
    window.addEventListener('resize', setSize);
    return () => window.removeEventListener('resize', setSize);
  }, [setSize]);

  // Render canvas
  return <canvas ref={canvas} className={styles.blobs} />;
}
